import { ConsoleLogAdapter } from 'library/ts/utilities/logger/adapters/ConsoleLogAdapter';
import { ILogAdapter } from 'library/ts/utilities/logger/interfaces/adapters/ILogAdapter';
import { ILogAdapterClass } from 'library/ts/utilities/logger/interfaces/adapters/ILogAdapterClass';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { ILogServiceDependencies } from 'library/ts/utilities/logger/interfaces/ILogServiceDependencies';
import { ILoggerOptions } from 'library/ts/utilities/logger/interfaces/ILoggerOptions';
import { LOGGER_ADAPTERS, LogMethods } from 'library/ts/utilities/logger/LogServiceConstants';
import { TLoggableList } from 'library/ts/utilities/logger/types/TLoggableList';

/**
 * @copyright © Paramount Streaming
 * @author  Jeff Appis
 * @description Loggers are created by LogService.create() to allow any part of
 * the application code to store a reference to a customized logger instance.
 * Logger provides the same API as console but routes all methods to an array of
 * log adapters which each have varying implementations of logging to serve
 * different use cases.  Logger instantiation reads a provided options object
 * parameter to customize the instance for the caller, such as by adding prefix
 * strings to each log message or by only logging to specific adapters.
 */
export class Logger implements ILogger {

	options: ILoggerOptions;
	availableAdapters: { [key in LOGGER_ADAPTERS]?: ILogAdapterClass } = {
		[ LOGGER_ADAPTERS.CONSOLE ]: ConsoleLogAdapter
	};

	/**
	 * @description Runtime dependencies injected by {@link LogService.create} to avoid
	 * a circular import — Logger needs LogService's timeElapsed and methodIsAvailable,
	 * while LogService needs Logger to instantiate it.
	 */
	#deps: ILogServiceDependencies;

	constructor( deps: ILogServiceDependencies, options: ILoggerOptions = {} ) {
		this.options = Object.assign( {}, this.defaultOptions, options );
		this.#deps = deps;
	}

	/**
	 * @description Returns reference to global Utilities
	 * @returns {Utilities | undefined}
	 */
	get utils() {
		return window?.PARA?.PlayerServices?.Utilities;
	}

	get defaultOptions() {
		return {
			prefixes: [],
			adapters: [ LOGGER_ADAPTERS.CONSOLE ],
			noTimestamp: false
		};
	}

	#adapters: { [key in LOGGER_ADAPTERS]?: ILogAdapter };
	get adapters() {
		if ( this.utils.Type.isEmpty( this.#adapters ) ) {
			this.#adapters = {};
			this.options.adapters.forEach( ( adapterKey ) => {
				this.#adapters[ adapterKey ] = this.availableAdapters[ adapterKey ].create();
			} );
		}
		return this.#adapters;
	}

	get prefixTimestamp() {
		const msFromStart = this.#deps.getTimeElapsed();
		const secFromStart = msFromStart / 1000;
		return '(T+' + secFromStart + 'sec)';
	}

	/**
	 * Calls the specified Log Method on each loaded LogAdapter, supplying it with
	 * the remaining sent parameters as messages.
	 * @param {string} method
	 * @param {TLoggable} messages
	 */
	#callAdapters = ( method: LogMethods, ...messages: TLoggableList ) => {
		if ( !this.#deps.methodIsAvailable( method ) ) { return; }

		Object.keys( this.adapters ).forEach( ( adapterKey ) => {
			const adapter = this.adapters[ adapterKey ];

			if ( !this.utils.Type.isNullOrUndefined( adapter?.[ method ] ) && this.utils.Type.isFunction( adapter?.[ method ] ) && messages ) {
				// assemble the messages to be logged
				const toLog = [
					...( !this.options?.noTimestamp ? [ this.prefixTimestamp ] : [] ),
					...( this.options.prefixes || [] ),
					...( messages || [] )
				];
				// feed log messages to adapter method
				adapter[ method ]( ...toLog );
			}
		} );
	};

	log( ...messages ) {
		return this.#callAdapters( LogMethods.LOG, ...messages );
	}

	info( ...messages ) {
		return this.#callAdapters( LogMethods.INFO, ...messages );
	}

	warn( ...messages ) {
		return this.#callAdapters( LogMethods.WARN, ...messages );
	}

	error( ...messages ) {
		return this.#callAdapters( LogMethods.ERROR, ...messages );
	}

	table( ...messages ) {
		return this.#callAdapters( LogMethods.TABLE, ...messages );
	}

	group( ...messages ) {
		return this.#callAdapters( LogMethods.GROUP, ...messages );
	}

	groupCollapsed( ...messages ) {
		return this.#callAdapters( LogMethods.GROUP_COLLAPSED, ...messages );
	}

	groupEnd( ...messages ) {
		return this.#callAdapters( LogMethods.GROUP_END, ...messages );
	}
}